// /my - Malaysia country home. Rendered by components/market/MarketHomePage
// with the Singapore homepage's sections, so the shape follows those sections.
// Copy is written for Malaysia: "aircond" first ("aircon" only as a secondary
// variant in body copy), Kuala Lumpur and the Klang Valley, nothing lifted
// from /sg.
const content = {
    title: (c) => `Aircond Service & Personal Trainers in KL | StringsSG Malaysia`,
    description: (c) =>
        `Aircond service from ${c.money(c.tables.general[0].wall)} and personal trainers from ${c.money(c.lowestPt, 0)} a session in Kuala Lumpur and the Klang Valley. Fixed prices.`,
    heroAlt: 'StringsSG home services for Kuala Lumpur residents',

    hero: {
        title: 'Aircond Service & Personal Trainers',
        highlight: 'in Kuala Lumpur',
        sub: (c) =>
            `Fixed prices for aircond general service, chemical wash, overhaul, gas top up and repairs, and certified personal trainers for your condo gym or home. Aircond service from ${c.money(c.tables.general[0].wall)}.`,
        badges: ['Fixed Prices', 'Muslimah-Friendly Trainers', 'Condo-Ready'],
    },

    services: {
        title: 'Two Services for KL Homes, Done Properly',
        intro: 'Aircond care and personal training are what KL households ask us for most, so that is where we started.',
        aircon: {
            title: 'Aircond Service KL',
            description: 'General service, chemical wash, chemical overhaul, gas top up, repairs and yearly servicing packages, for wall-mounted and ceiling cassette units.',
            ctaText: 'See aircond service prices in KL',
            imageAlt: 'Aircond technician arriving for a service in a KL home',
        },
        pt: {
            title: 'Personal Trainers KL',
            description: 'One-to-one training in your condo gym, at home or in the park, including female and muslimah-friendly trainers.',
            ctaText: 'See personal trainer prices in KL',
            imageAlt: 'Female personal trainer coaching a client in a gym',
        },
    },

    // One column per service, plus areas and guides (components/index/
    // ModernHomePage LocalServicesSection). Links come from data/markets/registry.js.
    local: {
        'kuala-lumpur': {
            aircon: {
                heading: 'Aircond Service in Kuala Lumpur',
                body: (c) =>
                    `KL's humidity keeps coils wet all year, which is where the smell, drips and weak airflow come from. Our technicians service wall-mounted and cassette units in condos and landed homes, from ${c.money(c.tables.general[0].wall)} for one unit, with every price listed before you book.`,
            },
            pt: {
                heading: 'Personal Trainer in Kuala Lumpur',
                body: (c) =>
                    `Certified trainers from ${c.money(c.lowestPt, 0)} a session for weight loss, strength, pantang recovery and warga emas. Train in your condo gym, at home or in the park - with plans that work around mamak, hawker and festive food.`,
            },
            areas: {
                heading: 'Areas We Cover in the Klang Valley',
                body: 'Condos in Mont Kiara, KLCC and Bangsar, landed homes in TTDI, Cheras and Damansara Heights, and Petaling Jaya and Subang Jaya next door. Each area page lists local building notes and the same fixed prices.',
            },
            guides: {
                heading: 'Guides for KL Homes',
                body: 'Practical answers for aircond owners and anyone starting to train in KL, with key guides in Bahasa Melayu too.',
            },
        },
    },

    trust: {
        title: 'Made for Malaysian Homes',
        intro: 'Year-round humidity, condo management rules and festive seasons all shape how a service should run here. Ours are set up for them.',
        items: [
            { title: 'Fixed Prices', body: 'Every aircond service and every training package is priced on the page - no surprise top ups on the day' },
            { title: 'Condo-Ready', body: 'Technician and trainer details sent in advance for your management office and guardhouse' },
            { title: 'Muslimah-Friendly', body: 'Female trainers for private sessions at home, scheduled around solat and Ramadan' },
            { title: 'Bahasa Melayu Too', body: 'Key guides are available in Bahasa Melayu' },
        ],
        card: {
            title: 'Lower TNB Bills',
            body: 'A clean aircond cools faster and runs less. Regular servicing keeps the room cold and the electricity bill down.',
        },
    },

    stepsTitle: 'Booking in Four Steps',
    steps: [
        { title: 'Pick your service', body: 'Choose an aircond service and units, or a training package - all prices shown.' },
        { title: 'Send your request', body: 'Tell us your area and a time that suits you.' },
        {
            title: (c) => (c.market.paymentsEnabled ? 'Pay online' : 'Confirm in the app'),
            body: (c) => (c.market.paymentsEnabled
                ? 'Pay in RM by card, Google Pay or Apple Pay in your StringsSG account - now, or after the service.'
                : 'Confirm your request in your StringsSG account - no card needed. We assign your technician or trainer.'),
        },
        { title: 'Done at your home', body: 'Anything extra is explained and agreed before it\'s done.' },
    ],

    ctaTitle: 'Ready for a Colder Room and a Fitter You?',
    ctaBody: 'Create your StringsSG account and book aircond service or a personal trainer in KL.',

    technology: {
        intro: 'The StringsSG platform, set up for Malaysia: prices in ringgit, bookings on KL time and our team a message away.',
        features: [
            { title: 'RedDotAI Assistant', description: 'Ask why your aircond is not cold or which training plan suits you, and get answers from our KL guides.' },
            { title: 'Prices in RM', description: 'Aircond and training prices are worked out in ringgit from your units or package, before you send a request.' },
            { title: 'Your Requests in One Place', description: 'Send, track and reschedule aircond visits and training sessions from your StringsSG account.' },
            {
                title: (c) => (c.market.paymentsEnabled ? 'Secure Payments' : 'No Card Needed to Book'),
                description: (c) => (c.market.paymentsEnabled
                    ? 'Pay by card, Apple Pay or Google Pay, encrypted with TLS 1.3 - now or after the service.'
                    : 'Confirm a request without a card. Our team arranges payment with you before the visit.'),
            },
            { title: 'Checked Professionals', description: 'Technicians and trainers are identity- and background-checked before their first job in Malaysia.' },
            { title: 'Reminders and Updates', description: 'Reminders when your next aircond service is due and live updates on the day.' },
        ],
        appPoints: [
            'Requests and confirmations for KL and the Klang Valley',
            'Chat with our Malaysia team',
            'Service reminders',
            'Your service history in one place',
        ],
    },

    partners: {
        title: 'Technicians and Trainers: Grow With StringsSG in KL',
        intro: 'We are building our team in Kuala Lumpur. You do the work; we bring the customers and run the scheduling and invoicing.',
        items: [
            { title: 'Customers Without Ad Spend', body: 'Requests from KL and Klang Valley homes come to you' },
            { title: 'Admin Handled', body: 'Scheduling, reminders and invoices run on our platform' },
            { title: 'Reliable Payouts', body: 'Get paid for every completed job' },
            { title: 'A Profile That Grows', body: 'Ratings and reviews build your reputation with each job' },
        ],
    },

    guidesTitle: 'Guides for KL Residents',
    faqs: [
        {
            question: 'Which areas in Malaysia do you cover?',
            answer: 'Kuala Lumpur and the nearby Klang Valley, including Petaling Jaya and Subang Jaya.',
        },
        {
            question: 'How do I pay?',
            answer: (c) => (c.market.paymentsEnabled
                ? 'Online in your StringsSG account, in RM: by card, Google Pay or Apple Pay. Choose Pay Now, or Pay Later to pay after the service.'
                : 'Confirm your request in the StringsSG app - no card is needed when you book. Our team arranges payment with you before the visit.'),
        },
        {
            question: 'Who is StringsSG?',
            answer: 'StringsSG has run home services since 2018 - aircond servicing and personal training first among them - and now serves Kuala Lumpur.',
        },
    ],
}

export default content
